import {
  getInboundJourneys,
  getNonTripOffers,
  getOutboundJourneys,
  getPurchaseFlowBookingAdditionalOffersSearch,
  getPurchaseFlowBookingById,
  getPurchaseFlowTravelAccountsMap,
  getSeatSelectionBookingById,
  resetPurchase,
  setSelectedInboundJourney,
  setSelectedInboundOfferMapByLegId,
  setSelectedOutboundJourney,
  setSelectedOutboundOfferMapByLegId,
  updateSelectedInboundOfferMapByLegId,
  updateSelectedNonTripOffer,
  updateSelectedOutboundOfferMapByLegId,
} from '@/features/purchase/purchaseActions';
import type {
  BookingDTO,
  SearchJourneyOffersDTO,
  SearchNonTripOfferDTO,
  SearchNonTripOffersDTO,
  SearchOffersDTO,
} from '@/types/dto/agent';
import type {
  AdditionalOffersCollection,
  OfferMapByLegId,
} from '@/types/offer';
import type { TravelAccountsMap } from '@/types/travelpass';
import { orderBookingTripsBasedOnDepartureTime } from '@/utils/booking';
import { createReducer } from '@reduxjs/toolkit';

type SearchOffers = SearchOffersDTO & {
  selectedJourney?: SearchJourneyOffersDTO;
  selectedOfferMapByLegId: OfferMapByLegId;
  additionalOffers: Array<AdditionalOffersCollection>;
};

type SearchNonTripOffers = SearchNonTripOffersDTO & {
  selectedNonTripOffer?: SearchNonTripOfferDTO;
};

type PurchaseState = {
  outbound: SearchOffers;
  inbound: SearchOffers;
  nonTripOffers: SearchNonTripOffers;
  booking: BookingDTO | null;
  travelAccountsMap?: TravelAccountsMap;
};

const initialState: PurchaseState = {
  outbound: {
    selectedOfferMapByLegId: {},
    additionalOffers: [],
  },
  inbound: {
    selectedOfferMapByLegId: {},
    additionalOffers: [],
  },
  nonTripOffers: {},
  booking: null,
};

export const purchaseReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(getOutboundJourneys.fulfilled, (state, action) => {
      state.outbound = { ...state.outbound, ...action.payload };
    })
    .addCase(getInboundJourneys.fulfilled, (state, action) => {
      state.inbound = { ...state.inbound, ...action.payload };
    })
    .addCase(getNonTripOffers.fulfilled, (state, action) => {
      state.nonTripOffers = { ...state.nonTripOffers, ...action.payload };
    })
    .addCase(setSelectedOutboundJourney, (state, action) => {
      state.outbound.selectedJourney = action.payload;
    })
    .addCase(setSelectedOutboundOfferMapByLegId, (state, action) => {
      state.outbound.selectedOfferMapByLegId = action.payload;
    })
    .addCase(updateSelectedOutboundOfferMapByLegId, (state, action) => {
      state.outbound.selectedOfferMapByLegId[action.payload.legId] =
        action.payload.offer;
    })
    .addCase(updateSelectedNonTripOffer, (state, action) => {
      state.nonTripOffers.selectedNonTripOffer = action.payload;
    })
    .addCase(setSelectedInboundJourney, (state, action) => {
      state.inbound.selectedJourney = action.payload;
    })
    .addCase(setSelectedInboundOfferMapByLegId, (state, action) => {
      state.inbound.selectedOfferMapByLegId = action.payload;
    })
    .addCase(updateSelectedInboundOfferMapByLegId, (state, action) => {
      state.inbound.selectedOfferMapByLegId[action.payload.legId] =
        action.payload.offer;
    })
    .addCase(resetPurchase, (state) => {
      Object.assign(state, initialState);
    })
    .addCase(
      getPurchaseFlowBookingAdditionalOffersSearch.fulfilled,
      (state, action) => {
        state.outbound.additionalOffers =
          action.payload.outboundAdditionalOffers;
        state.inbound.additionalOffers = action.payload.inboundAdditionalOffers;
      }
    )
    .addCase(getPurchaseFlowTravelAccountsMap.fulfilled, (state, action) => {
      state.travelAccountsMap = action.payload;
    })
    .addMatcher(
      (action) =>
        [
          getPurchaseFlowBookingById.fulfilled.type,
          getSeatSelectionBookingById.fulfilled.type,
        ].includes(action.type),
      (state, action) => {
        state.booking = orderBookingTripsBasedOnDepartureTime(action.payload);
      }
    );
});
